import React, { useState } from 'react';
import { Box, IconButton, TextField, useTheme, Typography, alpha } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PsychologyIcon from '@mui/icons-material/Psychology';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { styled } from '@mui/system';
import useChatEvents from '../hooks/useChatEvents';
import useFileUpload from '../hooks/useFileUpload';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useTranslation } from '../../hooks/useTranslation';
import { useLanguageContext } from '../../context/LanguageContext';
import VoiceButton from './VoiceButton';
import { toggleThinkingMode } from '../../store/slices/modelSlice';
import { isThinkingCapable, getThinkingCapabilityInfo } from '../../utils/modelCapabilities';
import { toggleWebResearch, toggleDeepResearch } from '../../store/slices/researchSlice';

// Define the CustomTextField component
const CustomTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'transparent',
    },
    '&:hover fieldset': {
      borderColor: 'transparent',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'transparent',
    },
  },
  backgroundColor: 'transparent',
  width: '100%',
  margin: 'auto',
}));

const CustomInputField: React.FC = () => {
  const theme = useTheme();
  const [input, setInput] = useState('');
  const { sendUserInput } = useChatEvents();
  const { t } = useTranslation();
  const { isArabic } = useLanguageContext();
  const { handleFileSelect } = useFileUpload();
  const dispatch = useDispatch();
  const selectedModel = useSelector((state: RootState) => state.model.selectedModel);
  const thinkingMode = useSelector((state: RootState) => state.model.thinkingMode);
  const researchMode = useSelector((state: RootState) => state.research.researchMode);
  
  // Check if current model supports thinking mode
  const thinkingCapability = getThinkingCapabilityInfo(selectedModel || '');
  const showThink = isThinkingCapable(selectedModel || '') && selectedModel === 'glm-4.5';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedInput = input.trim();
    if (trimmedInput) {
      try {
        sendUserInput(trimmedInput);
        setInput('');
      } catch (error) {
        console.error('Error sending message:', error);
      }
    }
  };

  const handleFileUpload = () => {
    document.getElementById('fileInput')?.click();
  };

  return (
    <form onSubmit={handleSubmit} className='relative' dir={isArabic ? 'rtl' : 'ltr'}
    style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
    }}
    >
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        sx={{
          marginBottom: 1,          
          backgroundColor: alpha(theme.palette.background.paper, 0.1),
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          filter: 'blur(0px)',
          boxShadow: `0 8px 22px ${alpha(theme.palette.common.black, 0.15)}`,
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          padding: '0 8px',
          paddingBottom: '10px',
          margin: '0px',
          position: 'absolute',
          width: '70%',
          bottom: '0',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
        }}
      >
        {/* Top Row - Text Input */}
        <CustomTextField
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t('chatPlaceholder')}
          multiline
          minRows={2}
          maxRows={8}
          dir={isArabic ? 'rtl' : 'ltr'}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
          
          sx={{
            width: '100%',
            margin: '0px',
            borderRadius: '20px',
            '& .MuiInputBase-input': {
              direction: isArabic ? 'rtl' : 'ltr',
              textAlign: isArabic ? 'right' : 'left',
              fontFamily: isArabic ? 'Arial, sans-serif' : 'inherit',
            },
          }}
          InputProps={{
            style: { 
              borderRadius: 20, 
              border: 'none', 
              color: theme.palette.primary.contrastText,
              boxShadow: 'none',
              direction: isArabic ? 'rtl' : 'ltr',
            },
          }}
        />
        
        {/* Bottom Row - Send, File Upload, and Microphone Buttons */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            width: '100%',
            paddingX: '8px',
          }}
        >
          {isArabic ? (
            <>
              {/* Arabic layout: Send button on right, File upload and Mic on left */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <VoiceButton 
                  onTranscriptionComplete={(text) => {
                    setInput(text);
                    // Just set the input text without auto-sending
                  }}
                  disabled={false}
                />
                {/* File Upload Button - Matching Voice Button Style */}
                <Box display="flex" alignItems="center" sx={{ mr: 1 }}>
                  <Box
                    className='flex items-center border cursor-pointer'
                    onClick={handleFileUpload}
                    style={{
                      borderColor: alpha(theme.palette.text.secondary, 0.2),
                      borderRadius: '35px',
                      padding: '7px 9px',
                      backgroundColor: alpha(theme.palette.background.paper, 1),
                      backdropFilter: 'blur(12px)',
                      WebkitBackdropFilter: 'blur(12px)',
                      gap: '2px',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                    sx={{
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.action.hover, 0.1),
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        transform: 'scale(1.02)',
                      },
                      '&:active': {
                        backgroundColor: alpha(theme.palette.action.selected, 0.2),
                        transform: 'scale(0.98)',
                      },
                    }}
                  >
                    <IconButton
                      sx={{
                        color: 'text.secondary',
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                        '&:active': {
                          backgroundColor: 'action.selected',
                        },
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AttachFileIcon sx={{ fontSize: '19px' }} />
                    </IconButton>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>File</Typography>
                  </Box>
                </Box>

                {/* Web Research Button */}
                <Box display="flex" alignItems="center" sx={{ mr: 1 }}>
                  <Box
                    className='flex items-center border cursor-pointer'
                    onClick={() => dispatch(toggleWebResearch())}
                    style={{
                      borderColor: researchMode === 'web'
                        ? alpha('#ffffff', 0.9)
                        : alpha(theme.palette.text.secondary, 0.2),
                      backgroundColor: researchMode === 'web'
                        ? alpha(theme.palette.background.paper, 0.05)
                        : alpha(theme.palette.background.paper, 1),
                      borderRadius: '35px',
                      padding: '7px 9px',
                      backdropFilter: 'blur(12px)',
                      WebkitBackdropFilter: 'blur(12px)',
                      boxShadow: researchMode === 'web'
                        ? `0 4px 8px rgba(255, 255, 255, 0.2), 0 0 0 1px ${alpha('#ffffff', 0.6)}`
                        : '0 4px 6px rgba(0, 0, 0, 0.1)',
                      gap: '2px',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                    sx={{
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.action.hover, 0.1),
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        transform: 'scale(1.02)',
                      },
                      '&:active': {
                        backgroundColor: alpha(theme.palette.action.selected, 0.2),
                        transform: 'scale(0.98)',
                      },
                    }}
                  >
                    <IconButton
                      sx={{
                        color: researchMode === 'web' ? alpha('#ffffff', 0.95) : 'text.secondary',
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                        '&:active': {
                          backgroundColor: 'action.selected',
                        },
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <TravelExploreIcon sx={{ fontSize: '19px' }} />
                    </IconButton>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '13px',
                        color: researchMode === 'web' ? alpha('#ffffff', 0.95) : 'text.secondary'
                      }}
                    >
                      Web Research
                    </Typography>
                  </Box>
                </Box>

                {/* Deep Research Button */}
                <Box display="flex" alignItems="center" sx={{ mr: 1 }}>
                  <Box
                    className='flex items-center border cursor-pointer'
                    onClick={() => dispatch(toggleDeepResearch())}
                    style={{
                      borderColor: researchMode === 'deep'
                        ? alpha('#ffffff', 0.9)
                        : alpha(theme.palette.text.secondary, 0.2),
                      backgroundColor: researchMode === 'deep'
                        ? alpha(theme.palette.background.paper, 0.05)
                        : alpha(theme.palette.background.paper, 1),
                      borderRadius: '35px',
                      padding: '7px 9px',
                      backdropFilter: 'blur(12px)',
                      WebkitBackdropFilter: 'blur(12px)',
                      boxShadow: researchMode === 'deep'
                        ? `0 4px 8px rgba(255, 255, 255, 0.2), 0 0 0 1px ${alpha('#ffffff', 0.6)}`
                        : '0 4px 6px rgba(0, 0, 0, 0.1)',
                      gap: '2px',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                    sx={{
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.action.hover, 0.1),
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        transform: 'scale(1.02)',
                      },
                      '&:active': {
                        backgroundColor: alpha(theme.palette.action.selected, 0.2),
                        transform: 'scale(0.98)',
                      },
                    }}
                  >
                    <IconButton
                      sx={{
                        color: researchMode === 'deep' ? alpha('#ffffff', 0.95) : 'text.secondary',
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                        '&:active': {
                          backgroundColor: 'action.selected',
                        },
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ManageSearchIcon sx={{ fontSize: '19px' }} />
                    </IconButton>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '13px',
                        color: researchMode === 'deep' ? alpha('#ffffff', 0.95) : 'text.secondary'
                      }}
                    >
                      Deep Research
                    </Typography>
                  </Box>
                </Box>

                {/* Think Toggle - Only show when GLM-4.5 is selected */}
                {showThink && (
                  <Box 
                    display="flex" 
                    alignItems="center"
                    title={thinkingCapability.tooltip}
                  >
                    <Box
                      className='flex items-center border cursor-pointer'
                      onClick={() => {
                        try {
                          dispatch(toggleThinkingMode(!thinkingMode));
                        } catch (error) {
                          console.error('Error toggling thinking mode:', error);
                        }
                      }}
                      style={{
                        borderColor: thinkingMode 
                          ? alpha(theme.palette.primary.main, 0.5) 
                          : alpha(theme.palette.text.secondary, 0.2),
                        backgroundColor: thinkingMode
                          ? alpha(theme.palette.primary.main, 0.15)
                          : alpha(theme.palette.background.paper, 1),
                        borderRadius: '35px',
                        padding: '7px 10px',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        boxShadow: thinkingMode 
                          ? `0 4px 6px rgba(0, 0, 0, 0.1), 0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                          : '0 4px 6px rgba(0, 0, 0, 0.1)',
                        gap: '6px',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      <IconButton
                        sx={{
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          borderRadius: '50%',
                          width: 20,
                          height: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <PsychologyIcon sx={{ 
                          fontSize: '19px',
                          transition: 'all 0.2s ease-in-out',
                          transform: thinkingMode ? 'scale(1.1)' : 'scale(1)',
                          color: thinkingMode ? 'primary.main' : 'inherit',
                        }} />
                      </IconButton>
                      <Typography
                        variant="body2"
                        sx={{ 
                          fontSize: '13px', 
                          color: thinkingMode ? 'primary.main' : 'text.secondary'
                        }}
                      >
                        Think
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
              <IconButton
                type="submit"
                disabled={!input || !input.trim()}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    color: '#03a9f4',
                    '& .MuiSvgIcon-root': {
                      transform: 'scale(1.1)',
                    },
                  },
                  '&:active': {
                    backgroundColor: 'action.selected',
                  },
                  '&:disabled': {
                    backgroundColor: 'transparent',
                    color: 'action.disabled',
                  },
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  borderRadius: '50%',
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <SendIcon />
              </IconButton>
            </>
          ) : (
            <>
              {/* English layout: File upload and Mic on left, Send button on right */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <VoiceButton 
                  onTranscriptionComplete={(text) => {
                    setInput(text);
                    // Just set the input text without auto-sending
                  }}
                  disabled={false}
                />
                {/* File Upload Button - Matching Voice Button Style */}
                <Box display="flex" alignItems="center" sx={{ mr: 1 }}>
                  <Box
                    className='flex items-center border cursor-pointer'
                    onClick={handleFileUpload}
                    style={{
                      borderColor: alpha(theme.palette.text.secondary, 0.2),
                      borderRadius: '35px',
                      padding: '7px 9px',
                      backgroundColor: alpha(theme.palette.background.paper, 1),
                      backdropFilter: 'blur(12px)',
                      WebkitBackdropFilter: 'blur(12px)',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      gap: '2px',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                    sx={{
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.action.hover, 0.1),
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        transform: 'scale(1.02)',
                      },
                      '&:active': {
                        backgroundColor: alpha(theme.palette.action.selected, 0.2),
                        transform: 'scale(0.98)',
                      },
                    }}
                  >
                    <IconButton
                      sx={{
                        color: 'text.secondary',
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                        '&:active': {
                          backgroundColor: 'action.selected',
                        },
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AttachFileIcon sx={{ fontSize: '19px' }} />
                    </IconButton>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>File</Typography>
                  </Box>
                </Box>

                {/* Web Research Button */}
                <Box display="flex" alignItems="center" sx={{ mr: 1 }}>
                  <Box
                    className='flex items-center border cursor-pointer'
                    onClick={() => dispatch(toggleWebResearch())}
                    style={{
                      borderColor: researchMode === 'web'
                        ? alpha('#ffffff', 0.9)
                        : alpha(theme.palette.text.secondary, 0.2),
                      backgroundColor: researchMode === 'web'
                        ? alpha(theme.palette.background.paper, 0.05)
                        : alpha(theme.palette.background.paper, 1),
                      borderRadius: '35px',
                      padding: '7px 9px',
                      backdropFilter: 'blur(12px)',
                      WebkitBackdropFilter: 'blur(12px)',
                      boxShadow: researchMode === 'web'
                        ? `0 4px 8px rgba(255, 255, 255, 0.2), 0 0 0 1px ${alpha('#ffffff', 0.6)}`
                        : '0 4px 6px rgba(0, 0, 0, 0.1)',
                      gap: '2px',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                    sx={{
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.action.hover, 0.1),
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        transform: 'scale(1.02)',
                      },
                      '&:active': {
                        backgroundColor: alpha(theme.palette.action.selected, 0.2),
                        transform: 'scale(0.98)',
                      },
                    }}
                  >
                    <IconButton
                      sx={{
                        color: researchMode === 'web' ? alpha('#ffffff', 0.95) : 'text.secondary',
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                        '&:active': {
                          backgroundColor: 'action.selected',
                        },
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <TravelExploreIcon sx={{ fontSize: '19px' }} />
                    </IconButton>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '13px',
                        color: researchMode === 'web' ? alpha('#ffffff', 0.95) : 'text.secondary'
                      }}
                    >
                      Web Research
                    </Typography>
                  </Box>
                </Box>

                {/* Deep Research Button */}
                <Box display="flex" alignItems="center" sx={{ mr: 1 }}>
                  <Box
                    className='flex items-center border cursor-pointer'
                    onClick={() => dispatch(toggleDeepResearch())}
                    style={{
                      borderColor: researchMode === 'deep'
                        ? alpha('#ffffff', 0.9)
                        : alpha(theme.palette.text.secondary, 0.2),
                      backgroundColor: researchMode === 'deep'
                        ? alpha(theme.palette.background.paper, 0.05)
                        : alpha(theme.palette.background.paper, 1),
                      borderRadius: '35px',
                      padding: '7px 9px',
                      backdropFilter: 'blur(12px)',
                      WebkitBackdropFilter: 'blur(12px)',
                      boxShadow: researchMode === 'deep'
                        ? `0 4px 8px rgba(255, 255, 255, 0.2), 0 0 0 1px ${alpha('#ffffff', 0.6)}`
                        : '0 4px 6px rgba(0, 0, 0, 0.1)',
                      gap: '2px',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                    sx={{
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.action.hover, 0.1),
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        transform: 'scale(1.02)',
                      },
                      '&:active': {
                        backgroundColor: alpha(theme.palette.action.selected, 0.2),
                        transform: 'scale(0.98)',
                      },
                    }}
                  >
                    <IconButton
                      sx={{
                        color: researchMode === 'deep' ? alpha('#ffffff', 0.95) : 'text.secondary',
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                        '&:active': {
                          backgroundColor: 'action.selected',
                        },
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ManageSearchIcon sx={{ fontSize: '19px' }} />
                    </IconButton>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '13px',
                        color: researchMode === 'deep' ? alpha('#ffffff', 0.95) : 'text.secondary'
                      }}
                    >
                      Deep Research
                    </Typography>
                  </Box>
                </Box>

                {/* Think Toggle - Only show when GLM-4.5 is selected */}
                {showThink && (
                  <Box 
                    display="flex" 
                    alignItems="center"
                    title={thinkingCapability.tooltip}
                  >
                    <Box
                      className='flex items-center border cursor-pointer'
                      onClick={() => {
                        try {
                          dispatch(toggleThinkingMode(!thinkingMode));
                        } catch (error) {
                          console.error('Error toggling thinking mode:', error);
                        }
                      }}
                      style={{
                        borderColor: thinkingMode 
                          ? alpha(theme.palette.primary.main, 0.5) 
                          : alpha(theme.palette.text.secondary, 0.2),
                        backgroundColor: thinkingMode
                          ? alpha(theme.palette.primary.main, 0.15)
                          : alpha(theme.palette.background.paper, 1),
                        borderRadius: '35px',
                        padding: '7px 10px',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        boxShadow: thinkingMode 
                          ? `0 4px 6px rgba(0, 0, 0, 0.1), 0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                          : '0 4px 6px rgba(0, 0, 0, 0.1)',
                        gap: '6px',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      <IconButton
                        sx={{
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          borderRadius: '50%',
                          width: 20,
                          height: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <PsychologyIcon sx={{ 
                          fontSize: '19px',
                          transition: 'all 0.2s ease-in-out',
                          transform: thinkingMode ? 'scale(1.1)' : 'scale(1)',
                          color: thinkingMode ? 'primary.main' : 'inherit',
                        }} />
                      </IconButton>
                      <Typography
                        variant="body2"
                        sx={{ 
                          fontSize: '13px', 
                          color: thinkingMode ? theme.palette.text.secondary : 'text.secondary'
                        }}
                      >
                        Think
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
              <IconButton
                type="submit"
                disabled={!input || !input.trim()}
                sx={{
                  color: 'text.primary',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    color: '#03a9f4',
                    '& .MuiSvgIcon-root': {
                      transform: 'scale(1.1)',
                    },
                  },
                  '&:active': {
                    backgroundColor: 'action.selected',
                  },
                  '&:disabled': {
                    backgroundColor: 'transparent',
                    color: 'action.disabled',
                  },
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  borderRadius: '50%',
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <SendIcon />
              </IconButton>
            </>
          )}
        </Box>
      </Box>
      
      {/* Hidden file input */}
      <input
        type="file"
        id="fileInput"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        accept=".pdf,.txt,.docx,.csv,.xlsx,.xlsm,.xls"
        multiple
      />
    </form>
  );
};

export default CustomInputField;